import React from 'react'

import { ChipSwitch, ChipSwitchButton } from '../../components/ChipSwitch'
import { useFinancialsTableContext } from '../../components/financials-table'
import { EarningsDataEnum, EarningsSettings, EarningsTimeframeEnum } from './types'

interface Props {
  timeframe: EarningsTimeframeEnum
  isSemiAnnual: boolean
  showTimeframe: boolean
  hasOldDesign?: boolean
  canShrinkLabels?: boolean
}

function GrowthLabel({
  isPercentage,
  isQuarterlyTimeframe,
  isSemiAnnual,
  canShrinkLabels,
}: {
  canShrinkLabels?: boolean
  isSemiAnnual?: boolean
  isQuarterlyTimeframe?: boolean
  isPercentage?: boolean
}) {
  const growthLabel = 'Growth'
  let timeframeLabel = 'YoY'
  if (isQuarterlyTimeframe) {
    timeframeLabel = isSemiAnnual ? 'SoS' : 'QoQ'
  }

  const longLabel = `${timeframeLabel} ${growthLabel} ${isPercentage ? '%' : ''}`

  if (!canShrinkLabels) {
    return longLabel
  }

  return (
    <>
      <span className="hidden [@media(min-width:1050px)]:inline">{longLabel}</span>
      <span className="[@media(min-width:1050px)]:hidden">
        {timeframeLabel} {isPercentage ? '%' : growthLabel}
      </span>
    </>
  )
}

export default function WidgetSettingsSection({
  timeframe,
  isSemiAnnual,
  showTimeframe,
  canShrinkLabels = false,
  hasOldDesign = false,
}: Props) {
  const { settings, setSettings } = useFinancialsTableContext<EarningsSettings>()

  const toggleSetting = (key: keyof EarningsSettings, value: EarningsSettings[keyof EarningsSettings]) => {
    setSettings({
      ...settings,
      [key]: value,
    })
  }

  const isQuarterlyTimeframe = timeframe === EarningsTimeframeEnum.quarterly // EarningsTimeframeEnum.quarterly is used for both quarterly & semiannual

  return (
    <div className="flex flex-row space-x-5">
      <ChipSwitch size="small" label="Data" rounding={hasOldDesign ? 'none' : undefined}>
        {isQuarterlyTimeframe ? (
          <ChipSwitchButton
            active={settings[EarningsDataEnum.extraChangeYoYonQoQ]}
            onClick={() => {
              toggleSetting(EarningsDataEnum.extraChangeYoYonQoQ, !settings[EarningsDataEnum.extraChangeYoYonQoQ])
            }}
          >
            <GrowthLabel canShrinkLabels={canShrinkLabels} />
          </ChipSwitchButton>
        ) : null}
        {isQuarterlyTimeframe ? (
          <ChipSwitchButton
            active={settings[EarningsDataEnum.extraChangePctYoYonQoQ]}
            onClick={() => {
              toggleSetting(EarningsDataEnum.extraChangePctYoYonQoQ, !settings[EarningsDataEnum.extraChangePctYoYonQoQ])
            }}
          >
            <GrowthLabel canShrinkLabels={canShrinkLabels} isPercentage />
          </ChipSwitchButton>
        ) : null}
        <ChipSwitchButton
          active={settings[EarningsDataEnum.mainChange]}
          onClick={() => {
            toggleSetting(EarningsDataEnum.mainChange, !settings[EarningsDataEnum.mainChange])
          }}
        >
          <GrowthLabel
            canShrinkLabels={canShrinkLabels}
            isQuarterlyTimeframe={isQuarterlyTimeframe}
            isSemiAnnual={isSemiAnnual}
          />
        </ChipSwitchButton>
        <ChipSwitchButton
          active={settings[EarningsDataEnum.mainChangePct]}
          onClick={() => {
            toggleSetting(EarningsDataEnum.mainChangePct, !settings[EarningsDataEnum.mainChangePct])
          }}
        >
          <GrowthLabel
            canShrinkLabels={canShrinkLabels}
            isQuarterlyTimeframe={isQuarterlyTimeframe}
            isSemiAnnual={isSemiAnnual}
            isPercentage
          />
        </ChipSwitchButton>
      </ChipSwitch>

      {showTimeframe && (
        <ChipSwitch size="small" label="Timeframe" rounding={hasOldDesign ? 'none' : undefined}>
          <ChipSwitchButton
            active={timeframe === EarningsTimeframeEnum.annual}
            onClick={() => {
              toggleSetting('timeframe', EarningsTimeframeEnum.annual)
            }}
          >
            Annual
          </ChipSwitchButton>
          <ChipSwitchButton
            active={timeframe === EarningsTimeframeEnum.quarterly}
            onClick={() => {
              toggleSetting('timeframe', EarningsTimeframeEnum.quarterly)
            }}
          >
            {isSemiAnnual ? 'Semiannual' : 'Quarterly'}
          </ChipSwitchButton>
        </ChipSwitch>
      )}
    </div>
  )
}
